import React from "react";
import cloudStore from "../store/CloudStore";
import { Divider, Paper, Typography } from "@material-ui/core";
import CheckboxSetting from "../components/SettingFields/CheckboxSetting";
import NumberSetting from "../components/SettingFields/NumberSetting";
import HandheldPasswordSetting from "../components/SettingFields/HandheldPasswordSetting";
import TextSetting from "../components/SettingFields/TextSetting";
import withIntl from "../utils/withIntl";
import MultiTextSetting from "../components/SettingFields/MultiTextSetting";
import { Settings } from "@material-ui/icons";
import { updateMerchants } from "../store/MerchantApi";
import LanguageSetting from "../components/SettingFields/LanguageSetting";
import CurrencySetting from "../components/SettingFields/CurrencySetting";
import LanguagesSetting from "../components/SettingFields/LanguagesSetting";
import TimezoneSetting from "../components/SettingFields/TimezoneSetting";
import ArraySetting from "../components/SettingFields/ArraySetting";

const getMerchantSettings = (merchant) => {
  let languages = merchant.languages;
  if (!languages) {
    languages = [{ language: merchant.default_language, active: true }];
  }
  const settingGroups = [
    {
      key: "general_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "name", value: merchant.name, type: "text" },
        { key: "currency", value: merchant.currency, type: "currency" },
        { key: "default_language", value: merchant.default_language, type: "language" },
        {
          key: "languages",
          value: languages,
          type: "languages",
          getValues: (value) => {
            return value.reduce((options, option) => {
              if (option.active) {
                options.push(option.language);
              }
              return options;
            }, []);
          },
          setValues: (values, originalValues) => {
            values.forEach((language) => {
              if (
                !originalValues.some((languageObject) => {
                  return languageObject.language === language;
                })
              ) {
                originalValues.push({ language: language, active: false });
              }
            });
            return originalValues.map((language) => ({
              active: values.includes(language.language),
              language: language.language,
            }));
          },
        },
        { key: "timezone", value: merchant.timezone, type: "timezone" },
        { key: "log_email_minutes", value: merchant.log_email_minutes, type: "number" },
        {
          key: "log_email_addresses",
          value: merchant.log_email_addresses,
          type: "array",
          transformValue: (value) => {
            return value.reduce((values, value) => {
              if (value.length > 0) {
                values.push(value);
              }
              return values;
            }, []);
          },
        },
      ],
    },
  ];
  return settingGroups;
};

const getNewData = (setting, value) => {
  if (setting.transformValue) {
    value = setting.transformValue(value);
  }
  if (!setting.newDataKeys) {
    return { [setting.key]: value };
  } else {
    let object = {};
    let keys = setting.newDataKeys.reverse();
    keys.forEach((key, index) => {
      if (index === 0) {
        object[key] = value;
      } else {
        object = { [key]: object };
      }
    });
    return object;
  }
};

function GeneralSettings(props) {
  const merchant = cloudStore.merchant;
  const [settingGroups, setSettingGroups] = React.useState(getMerchantSettings(merchant));

  return (
    <div style={{ display: "flex", flexFlow: "wrap" }}>
      {settingGroups.map((settingGroup, index) => {
        return (
          <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {settingGroup.icon && settingGroup.icon}
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: settingGroup.key })}
              </Typography>
            </div>
            {settingGroup.settings.map((setting, index) => {
              switch (setting.type) {
                case "languages":
                  return (
                    <>
                      <LanguagesSetting
                        label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                        authenticationName={"merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          value = setting.setValues(value, setting.value);
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                    </>
                  );
                case "language":
                  return (
                    <>
                      <LanguageSetting
                        label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                        authenticationName={"merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                    </>
                  );
                case "currency":
                  return (
                    <>
                      <CurrencySetting
                        label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                        authenticationName={"merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                    </>
                  );
                case "timezone":
                  return (
                    <>
                      <TimezoneSetting
                        label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                        authenticationName={"merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                    </>
                  );
                case "boolean":
                  return (
                    <>
                      <CheckboxSetting
                        label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                        authenticationName={"sp_merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                      <Divider />
                    </>
                  );
                case "number":
                  return (
                    <>
                      <NumberSetting
                        label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                        authenticationName={"sp_merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </>
                  );
                case "number_password":
                  return (
                    <>
                      <HandheldPasswordSetting
                        label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                        authenticationName={"sp_merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </>
                  );
                case "text":
                  return (
                    <>
                      <TextSetting
                        label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                        authenticationName={"merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </>
                  );
                case "multitext":
                  return (
                    <>
                      <MultiTextSetting
                        label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                        authenticationName={"sp_merchant." + setting.key}
                        key={setting.key + "-" + merchant.updated_at}
                        setting={setting}
                        onChange={(value) => {
                          if (value === setting.value) {
                            return;
                          }
                          updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                            setSettingGroups(getMerchantSettings(merchants[0]));
                          });
                        }}
                      />
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </>
                  );
                case "array":
                  return (
                    <ArraySetting
                      label={props.intl.formatMessage({ id: "merchant." + setting.key })}
                      authenticationName={"merchant." + setting.key}
                      key={setting.key + "-" + merchant.updated_at}
                      setting={setting}
                      onChange={(value) => {
                        if (value === setting.value) {
                          return;
                        }

                        let newValue = setting.transformValue ? setting.transformValue(value) : value;
                        if (Array.isArray(value)) {
                          if (newValue.equals(setting.value)) {
                            return;
                          }
                        } else {
                          if (setting.originalValue != null) {
                            if (setting.originalValue === newValue) {
                              return;
                            }
                          } else {
                            if (setting.value === newValue) {
                              return;
                            }
                          }
                        }

                        updateMerchants([merchant], [getNewData(setting, value)]).then((merchants) => {
                          setSettingGroups(getMerchantSettings(merchants[0]));
                        });
                      }}
                    >
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </ArraySetting>
                  );
                case "todo":
                  return (
                    <>
                      <Typography variant={"h6"}>
                        Coming soon {props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                      </Typography>
                      <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                    </>
                  );
                default:
                  return null;
              }
            })}
          </Paper>
        );
      })}
    </div>
  );
}

export default withIntl(GeneralSettings);
